import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

import {
  REPORT_STATUS_COLOR_MAP,
  REPORT_STATUS_LABELS,
  REPORT_PRIORITY_COLOR_MAP,
  REPORT_PRIORITY_LABELS,
  REPORT_TARGET_TYPE_LABELS,
  REPORT_REASON_LABELS,
  formatKoreanDateTime,
} from '../types/report';
import {
  useGetReportById,
  useStartReview,
  useApproveReport,
  useRejectReport,
  type ApproveReportMutationBody,
  type RejectReportMutationBody,
  type StartReviewMutationBody,
} from '@edusense/report-client';
import { useAuth } from '@edusense/shared-auth-core';

export default function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();

  const reviewerId = auth?.user?.id ?? auth?.state?.user?.id ?? '';
  const reviewerName = auth?.user?.name ?? auth?.state?.user?.name ?? auth?.user?.email ?? '';

  const {
    data: report,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetReportById(id ?? '', {
    query: {
      enabled: Boolean(id),
    },
  });

  const startReview = useStartReview({
    mutation: {
      onSuccess: () => refetch(),
    },
  });

  const approveReport = useApproveReport({
    mutation: {
      onSuccess: () => refetch(),
    },
  });

  const rejectReport = useRejectReport({
    mutation: {
      onSuccess: () => refetch(),
    },
  });

  const handleStartReview = async () => {
    if (!id || !reviewerId || !reviewerName) {
      console.warn('검토자 정보가 필요합니다. 먼저 로그인 상태를 확인하세요.');
      return;
    }

    const payload: StartReviewMutationBody = { reviewerId, reviewerName };
    try {
      await startReview.mutateAsync({ id, data: payload });
    } catch (mutationError) {
      console.error('검토 시작 실패', mutationError);
    }
  };

  const handleApprove = async () => {
    if (!id || !reviewerId || !reviewerName) {
      console.warn('검토자 정보가 필요합니다. 먼저 로그인 상태를 확인하세요.');
      return;
    }
    const payload: ApproveReportMutationBody = {
      reviewerId,
      reviewerName,
      reviewNote: '신고 내용을 검토하여 조치를 완료했습니다.',
      action: 'NO_ACTION',
    };
    try {
      await approveReport.mutateAsync({ id, data: payload });
    } catch (mutationError) {
      console.error('승인 처리 실패', mutationError);
    }
  };

  const handleReject = async () => {
    if (!id || !reviewerId || !reviewerName) {
      console.warn('검토자 정보가 필요합니다. 먼저 로그인 상태를 확인하세요.');
      return;
    }
    const payload: RejectReportMutationBody = {
      reviewerId,
      reviewerName,
      reviewNote: '증거 불충분으로 신고를 기각했습니다.',
    };
    try {
      await rejectReport.mutateAsync({ id, data: payload });
    } catch (mutationError) {
      console.error('기각 처리 실패', mutationError);
    }
  };

  const isActionDisabled = !id || !report;

  // Helper mapping HeroUI colors to our new Badge variants
  const getBadgeVariant = (color: string) => {
    switch (color) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'danger': return 'destructive';
      case 'primary': return 'default';
      default: return 'secondary';
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => navigate('/reports')}
        className="mb-4 text-muted-foreground hover:text-foreground"
      >
        ← 목록으로 돌아가기
      </Button>

      <Card>
        <CardHeader className="flex flex-row justify-between items-center py-4 border-b">
          <CardTitle className="text-2xl font-bold">신고 상세 정보</CardTitle>
          {report && (
            <div className="flex gap-2">
              <Badge variant={getBadgeVariant(REPORT_STATUS_COLOR_MAP[report.status])}>
                {REPORT_STATUS_LABELS[report.status]}
              </Badge>
              <Badge variant={getBadgeVariant(REPORT_PRIORITY_COLOR_MAP[report.priority])}>
                {REPORT_PRIORITY_LABELS[report.priority]}
              </Badge>
            </div>
          )}
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {isLoading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> 신고 정보를 불러오는 중입니다...
            </div>
          )}

          {isError && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
              <p className="font-semibold">신고 정보를 불러오지 못했습니다.</p>
              <p className="text-sm opacity-80">{error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'}</p>
              <div className="mt-3 flex justify-end">
                <Button variant="outline" size="sm" onClick={() => refetch()}>재시도</Button>
              </div>
            </div>
          )}

          {report && !isLoading && !isError && (
            <>
              {/* 기본 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DetailItem label="신고 ID" value={report.id} />
                <DetailItem label="대상 타입" value={REPORT_TARGET_TYPE_LABELS[report.targetType]} />
                <DetailItem label="대상 ID" value={report.targetId} />
                <DetailItem label="대상 제목" value={report.targetTitle ?? '-'} />
                <DetailItem label="신고자" value={report.reporterName} />
                <DetailItem label="신고자 이메일" value={report.reporterEmail ?? '-'} />
                <DetailItem label="검토자" value={report.reviewerName ?? '-'} />
                <DetailItem label="조치" value={report.action ?? '-'} />
              </div>

              <hr className="my-4 border-muted" />

              {/* 신고 내용 */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">신고 사유</p>
                  <p className="font-medium">{REPORT_REASON_LABELS[report.reason]}</p>
                </div>

                {report.description && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">상세 설명</p>
                    <p className="text-foreground whitespace-pre-wrap">{report.description}</p>
                  </div>
                )}

                {report.evidence && report.evidence.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">증거 자료</p>
                    <ul className="list-disc pl-5 space-y-1 text-primary">
                      {report.evidence.map((item) => (
                        <li key={item}>
                          <a href={item} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {item}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <hr className="my-4 border-muted" />

              {/* 시간 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DetailItem label="생성일" value={formatKoreanDateTime(report.createdAt)} />
                <DetailItem label="수정일" value={formatKoreanDateTime(report.updatedAt)} />
                <DetailItem label="검토 완료" value={formatKoreanDateTime(report.reviewedAt)} />
                <DetailItem label="해결 완료" value={formatKoreanDateTime(report.resolvedAt)} />
              </div>

              {report.moderationScore !== null && report.moderationScore !== undefined && (
                <div className="rounded-lg bg-muted px-4 py-3 text-sm">
                  <p className="font-semibold text-foreground">AI 모더레이션 점수</p>
                  <p className="text-muted-foreground">{Math.round(report.moderationScore * 100)}%</p>
                </div>
              )}

              <hr className="my-4 border-muted" />

              {/* 액션 버튼 */}
              <div className="flex flex-wrap gap-3 justify-end">
                {report.status === 'PENDING' && (
                  <div title={!reviewerId ? '검토자 정보를 불러올 수 없습니다. 로그인 상태를 확인하세요.' : undefined}>
                    <Button
                      onClick={handleStartReview}
                      disabled={isActionDisabled || !reviewerId || !reviewerName || startReview.isPending}
                    >
                      {startReview.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      검토 시작
                    </Button>
                  </div>
                )}

                {report.status === 'REVIEWING' && (
                  <>
                    <Button
                      variant="success"
                      onClick={handleApprove}
                      disabled={isActionDisabled || approveReport.isPending}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {approveReport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      승인
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleReject}
                      disabled={isActionDisabled || rejectReport.isPending}
                    >
                      {rejectReport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      기각
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}

interface DetailItemProps {
  label: string;
  value: string | number | null | undefined;
}

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium text-foreground">{value ?? '-'}</p>
    </div>
  );
}
